import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { ClassConstructor } from 'class-transformer';
import { Observable } from 'rxjs';

import { environment } from '@env/environment';
import { toModel } from '@misc/rxjs-operators/to-model.operator';
import { toModelsArray } from '@misc/rxjs-operators/to-models-array.operator';
import { HttpService, IServicesConfig } from '@services/http/http.service';

export type transition = 'cancel' | 'reject' | 'accept';

@Injectable({
  providedIn: 'root'
})
export abstract class ApiBaseAbstractService<T> {
  protected readonly _BASE_URL = environment.API_URL;
  protected _URLParams: string[] = [];
  protected abstract readonly _MODEL: ClassConstructor<T>;
  protected abstract readonly _URL_PATH: string;
  protected _URL_KEY?: string;

  protected constructor(protected readonly _http: HttpService) {}

  get url(): string {
    return this._BASE_URL + this._composeUrlPath();
  }

  getItems(params?: Params, servicesConfig?: IServicesConfig): Observable<T[]> {
    return this._http.get(this.url, { params }, servicesConfig).pipe(toModelsArray(this._MODEL));
  }

  getItem(id?: string, params?: Params, servicesConfig?: IServicesConfig): Observable<T> {
    return this._http.get(id ? `${this.url}/${id}` : this.url, { params }, servicesConfig).pipe(toModel(this._MODEL));
  }

  getItemEntity(moduleId: string, id?: string, params?: Params, servicesConfig?: IServicesConfig): Observable<T> {
    return this._http
      .get(id ? `${this.url}/${moduleId}/${this._URL_KEY}/${id}` : this.url, { params }, servicesConfig)
      .pipe(toModel(this._MODEL));
  }

  createItem(data: Params = {}, params?: Params, servicesConfig?: IServicesConfig): Observable<T> {
    const body: Params = { ...data };
    delete body.id;
    return this._http.post(this.url, body, { params }, servicesConfig).pipe(toModel(this._MODEL));
  }

  createItemEntity(moduleId: string, data: Params = {}, params?: Params, servicesConfig?: IServicesConfig): Observable<T> {
    const body: Params = { ...data };
    delete body.id;
    return this._http.post(`${this.url}/${moduleId}/${this._URL_KEY}`, body, { params }, servicesConfig).pipe(toModel(this._MODEL));
  }

  updateItem(data: Partial<T & { id: string }>, params?: Params, servicesConfig?: IServicesConfig): Observable<T> {
    const body: Partial<T & { id: string }> = { ...data };
    return this._http.patch(`${this.url}/${data.id}`, body, { params }, servicesConfig).pipe(toModel(this._MODEL));
  }

  updateItemEntity(moduleId: string, data: Partial<T & { id: string }>, params?: Params, servicesConfig?: IServicesConfig): Observable<T> {
    const body: Partial<T & { id: string }> = { ...data };
    return this._http
      .patch(`${this.url}/${moduleId}/${this._URL_KEY}/${data.id}`, body, { params }, servicesConfig)
      .pipe(toModel(this._MODEL));
  }

  deleteItem(id: string, servicesConfig?: IServicesConfig): Observable<void> {
    return this._http.delete(`${this.url}/${id}`, {}, servicesConfig);
  }

  deleteItemEntity(moduleId: string, id: string, servicesConfig?: IServicesConfig): Observable<void> {
    return this._http.delete(`${this.url}/${moduleId}/${this._URL_KEY}/${id}`, {}, servicesConfig);
  }

  getVersions(slug: string, params?: Params, servicesConfig?: IServicesConfig): Observable<T[]> {
    return this._http.get(`${this.url}/${slug}/versions`, { params }, servicesConfig);
  }

  private _composeUrlPath(): string {
    let URLPath: string = this._URL_PATH;

    if (this._URLParams?.length) {
      const params: string[] = URLPath.match(/:[a-z]+(?=\/)?/gi);
      params.forEach((param: string, i: number): void => {
        URLPath = URLPath.replace(param, this._URLParams[i]);
      });
    }

    return URLPath;
  }
}
